import { useState, useEffect, useRef } from 'preact/hooks';
import { t } from '../i18n';
import { getConfig, type LiveChatConfig, type PrivateContactConfig } from '../config';
import { navigateToView } from '../navigation';
import { openContextualLogin, openContextualSignup } from '../returnTo';
import { useChatStatus, type ChatStatus } from '../hooks/useChatStatus';
import { store } from '../store';
import { createTicket } from '../api';

/**
 * Provider-agnostic chat launcher.
 * External (phplive, deskpro): calls the global launch function injected by index.php.
 * Native (spcadmin LiveChat): dispatches CustomEvent for ChatBubble component.
 */
export function openChat(cfg?: LiveChatConfig): void {
  if (!cfg || cfg.enabled === false) return;
  if (cfg.provider === 'native') {
    document.dispatchEvent(new CustomEvent('sw:open-chat', { detail: cfg }));
    return;
  }
  const tryLaunch = (): boolean => {
    if (!cfg.launch_fn) return false;
    const fn = (window as any)[cfg.launch_fn];
    if (typeof fn !== 'function') return false;
    fn();
    return true;
  };
  if (tryLaunch()) return;

  // launch_fn is declared but function is not ready yet:
  // try to load provider script once and retry after load.
  if (cfg.launch_fn && cfg.script_url) {
    const key = `script[data-sw-chat-script="${cfg.script_url}"]`;
    const existing = document.querySelector<HTMLScriptElement>(key);
    if (existing) {
      existing.addEventListener('load', () => { tryLaunch(); }, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = cfg.script_url;
    script.setAttribute('data-sw-chat-script', cfg.script_url);
    script.addEventListener('load', () => { tryLaunch(); }, { once: true });
    const mount = cfg.button_id ? document.getElementById(cfg.button_id) : null;
    (mount ?? document.body).appendChild(script);
    return;
  }

  // Fallback for providers without launch function API.
  if (!cfg.launch_fn && cfg.button_id) {
    const btn = document.getElementById(cfg.button_id);
    if (btn instanceof HTMLElement) {
      btn.click();
    }
  }
}

interface ContactConfig {
  show_email?: boolean;
  show_hours?: boolean;
  show_tickets?: boolean;
  /** Store support email from backend render payload. */
  shop_email?: string;
  shop_uid?: string;
  /** Empty array = show all phones; otherwise only listed indices. */
  phone_indices?: number[];
  hours_mode?: 'site' | 'business_hours' | 'support_24_7' | 'custom' | string;
  /** Resolved custom hours for current locale (from block settings). */
  hours_custom?: string;
}

function resolveContactHours(
  contactCfg: ContactConfig,
  supportHours: string,
): string {
  const mode = (contactCfg.hours_mode ?? 'site').toLowerCase();
  if (mode === 'business_hours') {
    return t('contact.previewHours');
  }
  if (mode === 'support_24_7') {
    return t('contact.support247');
  }
  if (mode === 'custom') {
    return (contactCfg.hours_custom ?? '').trim();
  }
  const siteHours = supportHours.trim();
  if (siteHours !== '') return siteHours;
  return t('contact.support247');
}

function readContactConfig(): ContactConfig {
  try {
    const el = document.getElementById('shop-contact-page');
    const raw = el?.dataset?.contactConfig;
    if (raw) return JSON.parse(raw) as ContactConfig;
  } catch { /* ignore */ }
  return {};
}

function ChatStatusBadge({ status }: { status: ChatStatus }) {
  if (status === 'unknown') return null;
  const isOnline = status === 'online';
  return (
    <span style={`display:inline-flex;align-items:center;gap:4px;font-size:.8rem;color:${isOnline ? '#16a34a' : '#9ca3af'}`}>
      <span style={`width:7px;height:7px;border-radius:50%;background:${isOnline ? '#22c55e' : '#9ca3af'};display:inline-block`} />
      {isOnline ? t('chat.online', 'Online') : t('chat.offline', 'Offline')}
    </span>
  );
}

function getPhoneFlag(label?: string): string {
  const raw = (label ?? '').trim();
  if (!raw) return '';
  const key = raw.toUpperCase();
  const aliases: Record<string, string> = {
    USA: 'US',
    US: 'US',
    UNITEDSTATES: 'US',
    UK: 'GB',
    GB: 'GB',
    GREATBRITAIN: 'GB',
    GERMANY: 'DE',
    DE: 'DE',
  };
  const normalized = key.replace(/[^A-Z]/g, '');
  const code = aliases[normalized] ?? (normalized.length === 2 ? normalized : '');
  if (!/^[A-Z]{2}$/.test(code)) return '';
  const a = code.charCodeAt(0) - 65 + 0x1f1e6;
  const b = code.charCodeAt(1) - 65 + 0x1f1e6;
  return String.fromCodePoint(a, b);
}

function utf8ToBase64(value: string): string {
  const input = String(value || '').trim();
  if (!input) return '';
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function appendPhplivePageContext(url: URL): void {
  if (typeof window === 'undefined') return;
  const pageUrl = window.location.href.replace(/^http/i, 'hphp');
  const pg = utf8ToBase64(pageUrl);
  const tl = utf8ToBase64(document.title || '');
  if (pg) url.searchParams.set('pg', pg);
  if (tl) url.searchParams.set('tl', tl);
  url.searchParams.set('pgo', window.location.origin);
}

function buildLiveChatUrlWithPrefill(rawUrl: string, auth: any): string {
  const base = String(rawUrl || '').trim();
  if (!base) return '';
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(base, origin);
    appendPhplivePageContext(url);
    const firstName = String((auth as any)?.firstname ?? '').trim();
    const lastName = String((auth as any)?.lastname ?? '').trim();
    const fullName = `${firstName} ${lastName}`.trim()
      || String((auth as any)?.name ?? (auth as any)?.customer_name ?? (auth as any)?.login ?? '').trim();
    const email = String((auth as any)?.email ?? (auth as any)?.customer_email ?? '').trim();
    const encodedName = utf8ToBase64(fullName);
    const encodedEmail = utf8ToBase64(email);
    if (encodedName) url.searchParams.set('js_name', encodedName);
    if (encodedEmail) url.searchParams.set('js_email', encodedEmail);
    return url.toString();
  } catch {
    return base;
  }
}

function useWidgetSession() {
  const [snapshot, setSnapshot] = useState(() => ({
    auth: store.getAuth(),
    loading: store.getLoading(),
  }));
  useEffect(() => store.subscribe(() => {
    setSnapshot({ auth: store.getAuth(), loading: store.getLoading() });
  }), []);
  return snapshot;
}

function isAuthenticated(authUser: ReturnType<typeof store.getAuth>): boolean {
  if (!authUser) return false;
  return !!(authUser.login || authUser.customer_uid || authUser.customer_email);
}

function QuickContactForm() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const submissionPendingRef = useRef(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const { auth: authUser, loading } = useWidgetSession();
  const isLoggedIn = isAuthenticated(authUser);
  const authorName = (authUser?.name ?? authUser?.login ?? '').trim();
  const authorEmail = (authUser?.customer_email ?? '').trim();
  const authorLogin = (authUser?.login ?? '').trim();
  const isPreviewProxy = typeof window !== 'undefined' && window.location.pathname.includes('/preview-proxy');

  const submit = async (e: Event) => {
    e.preventDefault();
    if (submissionPendingRef.current) return;
    submissionPendingRef.current = true;
    let submissionSucceeded = false;
    try {
      if (!subject.trim() || !message.trim()) return;
      setSending(true);
      setError('');
      const config = getConfig();
      const shopUid = readContactConfig().shop_uid ?? config.siteKey ?? '';
      const res = await createTicket(config, subject, message, [], shopUid);
      if (res.status === 200) {
        submissionSucceeded = true;
        setSent(true);
      } else {
        setError(res.error || 'tickets.createFailed');
      }
    } catch {
      setError(t('api.networkError'));
    } finally {
      submissionPendingRef.current = submissionSucceeded;
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style="padding:0;min-height:160px" aria-busy="true">
        <h2 style="font-size:1.125rem;margin:0 0 1rem;font-weight:600">{t('contact.formTitle', 'Send us a message')}</h2>
        <p style="color:var(--sw-muted,#6c757d);font-size:14px;margin:0">{t('contact.loading', 'Loading…')}</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div style="padding:0">
        <h2 style="font-size:1.125rem;margin:0 0 1rem;font-weight:600">{t('contact.formTitle', 'Send us a message')}</h2>
        <p class="text-muted mb-3">
          {t('contact.loginRequired', 'Please log in to submit a support ticket.')}
        </p>
        <a href="#" class="btn btn-primary me-2 mb-2" onClick={(e) => { e.preventDefault(); openContextualLogin(); }}>
          {t('nav.login', 'Sign In')}
        </a>
        <a href="#" class="btn btn-outline-primary mb-2" onClick={(e) => { e.preventDefault(); openContextualSignup(); }}>
          {t('nav.signup', 'Create Account')}
        </a>
      </div>
    );
  }

  if (sent) {
    return (
      <div style="padding:0">
        <h2 style="font-size:1.125rem;margin:0 0 1rem;font-weight:600">{t('contact.formTitle', 'Send us a message')}</h2>
        <div style="padding:1.25rem;background:rgba(22,163,74,.07);border:1px solid rgba(22,163,74,.25);border-radius:var(--sw-radius,0.375rem);color:#15803d;font-size:14px;margin-bottom:1rem">
          ✓ {t('tickets.createSuccess', 'Your message has been sent. We will get back to you shortly.')}
        </div>
        <a href="#" onClick={(e) => { e.preventDefault(); navigateToView('tickets'); }}
          style="font-size:13px;color:var(--sw-primary,#0d6efd)">
          {t('contact.viewTickets', 'View my tickets →')}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style="padding:0">
      <h2 style="font-size:1.125rem;margin:0 0 1.25rem;font-weight:600">{t('contact.formTitle', 'Send us a message')}</h2>
      {!isPreviewProxy && (
        <div style="margin:0 0 1rem;padding:0.75rem 0.9rem;border:1px solid var(--sw-border,#dee2e6);border-radius:var(--sw-radius,0.375rem);background:rgba(13,110,253,.05);font-size:13px;color:var(--sw-text,#212529)">
          <strong style="display:block;margin-bottom:0.2rem">{t('contact.requestFrom', 'Request will be sent from')}</strong>
          <span>
            {authorName || authorLogin || t('contact.unknownUser', 'Authenticated user')}
            {authorEmail ? ` (${authorEmail})` : ''}
          </span>
        </div>
      )}

      {error && (
        <div style="margin-bottom:0.75rem;padding:0.6rem 0.9rem;background:rgba(220,38,38,.07);border:1px solid rgba(220,38,38,.25);border-radius:var(--sw-radius,0.375rem);color:#b91c1c;font-size:13px">
          {t(error, 'Failed to send. Please try again.')}
        </div>
      )}

      <label class="form-label mb-1">{t('contact.subject', 'Subject')}</label>
      <input class="form-control mb-3" value={subject} required
        onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
        placeholder={t('contact.subjectPlaceholder', 'How can we help?')} />

      <label class="form-label mb-1">{t('contact.message', 'Message')}</label>
      <textarea class="form-control mb-3" style="resize:vertical;min-height:120px" value={message} required
        onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
        placeholder={t('contact.messagePlaceholder', 'Describe your issue…')} />

      <button type="submit" class="btn btn-primary" disabled={sending}>
        {sending ? t('contact.sending', 'Sending…') : t('contact.send', 'Send Message')}
      </button>
    </form>
  );
}

export function ContactPage() {
  const config = getConfig();
  const support = config.support;
  const chatStatus = useChatStatus();
  const contactCfg = readContactConfig();
  const { auth: authUser } = useWidgetSession();

  if (!support) return <p class="text-muted">{t('contact.unavailable')}</p>;

  const chatEnabled = support.livechat && support.livechat.enabled !== false;
  const isNative = support.livechat?.provider === 'native';
  const liveChatOpenUrl = buildLiveChatUrlWithPrefill((support.livechat?.live_chat_url ?? '').trim(), authUser);
  const canOpenChatOverlay = isNative || !!support.livechat?.launch_fn || !!support.livechat?.script_url;

  const allPhones = Array.isArray(support.phones) ? support.phones : [];
  const phoneIdx = Array.isArray(contactCfg.phone_indices) ? contactCfg.phone_indices.map((v) => Number(v)).filter((v) => Number.isInteger(v) && v >= 0) : [];
  const phones = phoneIdx.length > 0
    ? phoneIdx.map((i) => allPhones[i]).filter((p): p is NonNullable<typeof p> => !!p)
    : allPhones;
  const emailContact = support.email_contact;
  const hoursValue = (support.working_hours ?? '').trim();
  const effectiveHours = resolveContactHours(contactCfg, hoursValue);

  const isLoggedIn = isAuthenticated(authUser);
  const showEmail   = contactCfg.show_email   !== false;
  const showHours   = contactCfg.show_hours   !== false;
  const showTickets = contactCfg.show_tickets  !== false;

  const itemStyle = 'margin-bottom:1rem;display:flex;gap:0.75rem;align-items:flex-start';
  const iconStyle = 'font-size:1.25rem;color:var(--sw-primary,#0d6efd)';

  const rightCol = (
    <div style="display:flex;flex-direction:column;gap:0">
      <h2 style="font-size:1.125rem;font-weight:600;margin:0 0 0.35rem">{t('contact.title', 'Contact Us')}</h2>

      {chatEnabled && (
        <div class="sw-contact-item" style={itemStyle}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={iconStyle}>
            <path d="M5 17l-1 4 4-1h8a7 7 0 1 0 0-14H8a7 7 0 0 0-3 11z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
          </svg>
          <div>
            <div style="display:flex;align-items:center;gap:.5rem;margin-bottom:0.25rem">
              <strong>{t('contact.live_chat')}</strong>
              {isNative && <ChatStatusBadge status={chatStatus} />}
            </div>
            {isNative && chatStatus === 'offline' ? (
              <NativeOfflineAction offlineAction={support.livechat!.offline_action} livechat={support.livechat!} />
            ) : canOpenChatOverlay ? (
              <a href="#" onClick={(e) => { e.preventDefault(); openChat(support.livechat); }}
                style="color:var(--sw-primary,#0d6efd)">
                {t('contact.start_chat')}
              </a>
            ) : liveChatOpenUrl ? (
              <a href={liveChatOpenUrl} target="_blank" rel="noreferrer noopener"
                onClick={(e) => {
                  const fn = (window as Window & { phpliveDirectLink?: (el: HTMLAnchorElement) => boolean }).phpliveDirectLink;
                  if (typeof fn === 'function') fn(e.currentTarget);
                }}
                style="color:var(--sw-primary,#0d6efd)">
                {t('contact.start_chat')}
              </a>
            ) : (
              <a href="#" onClick={(e) => { e.preventDefault(); openChat(support.livechat); }}
                style="color:var(--sw-primary,#0d6efd)">
                {t('contact.start_chat')}
              </a>
            )}
          </div>
        </div>
      )}

      {phones.some((p) => p.contact?.image_url && p.contact?.action_url) && (
        <div class="sw-contact-item" style={itemStyle}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={iconStyle}>
            <path d="M6.5 2.5h3L11 6l-2 2c1 2 2.5 3.5 4.5 4.5l2-2 3.5 1.5v3c0 1-1 1.8-2 1.7-7-.7-12.5-6.2-13.2-13.2-.1-1 .7-2 1.7-2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
          </svg>
          <div>
            <strong style="display:block;margin-bottom:0.25rem">{t('contact.phone')}</strong>
            {phones.map((p, idx) => {
              const flag = getPhoneFlag(p.label);
              if (!p.contact?.image_url || !p.contact?.action_url) return null;
              return (
                <div key={`phone-contact-${idx}`}>
                  <a href={p.contact.action_url} style="color:var(--sw-primary,#0d6efd);display:inline-flex;align-items:center;gap:0.25rem">
                    {flag ? <span aria-hidden="true">{flag}</span> : null}
                    <ContactImage contact={p.contact} height={16} />
                  </a>
                  {p.label && <span style="color:#6c757d"> ({p.label})</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showEmail && emailContact?.image_url && emailContact?.action_url && (
        <div class="sw-contact-item" style={itemStyle}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={iconStyle}>
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
            <path d="M4 7l8 6 8-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div>
            <strong style="display:block;margin-bottom:0.25rem">{t('contact.email')}</strong>
            <a href={emailContact.action_url} style="color:var(--sw-primary,#0d6efd)">
              <ContactImage contact={emailContact} height={16} />
            </a>
          </div>
        </div>
      )}

      {showHours && effectiveHours && (
        <div class="sw-contact-item" style={itemStyle}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={iconStyle}>
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
            <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div>
            <strong style="display:block;margin-bottom:0.25rem">{t('contact.hours')}</strong>
            <span>{effectiveHours}</span>
          </div>
        </div>
      )}

      {showTickets && (
        <div style="margin-top:1rem;padding-top:1rem;border-top:1px solid var(--sw-border,#eee)">
          {isLoggedIn ? (
            <>
              <p style="color:var(--sw-muted,#6c757d);margin-bottom:.75rem;font-size:14px">{t('contact.tickets_desc')}</p>
              <a href="#" onClick={(e) => { e.preventDefault(); navigateToView('tickets'); }}
                class="sl-btn"
                style="display:inline-flex;align-items:center;justify-content:center;padding:0.5rem 1rem;line-height:1.2;background:var(--sw-primary,#0d6efd);color:#fff;text-decoration:none;border-radius:var(--sw-radius,0.375rem)">
                {t('contact.open_ticket', 'Open a ticket')}
              </a>
            </>
          ) : (
            <p style="color:var(--sw-muted,#6c757d);font-size:13px">
              {t('contact.ticketsLoginRequired', 'Sign in to view and submit support tickets.')}
              {' '}
              <a href="#" onClick={(e) => { e.preventDefault(); openContextualLogin(); }}
                style="color:var(--sw-primary,#0d6efd)">
                {t('nav.login', 'Sign In')}
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div class="sw-contact-page" style="padding:2rem 1rem">
      <div class="sw-contact-page__grid">
        <div class="sw-contact-form-col" style="padding-top:1.5rem">
          <QuickContactForm />
        </div>
        <div class="sw-contact-info-col" style="background:var(--sw-bg,#f8f9fa);border-radius:10px;padding:1.5rem">
          {rightCol}
        </div>
      </div>
    </div>
  );
}

function ContactImage({ contact, height = 16 }: { contact: PrivateContactConfig; height?: number }) {
  if (!contact.image_url) return null;
  return <img src={contact.image_url} alt="" aria-hidden="true" style={`display:inline-block;width:auto;height:${height}px;vertical-align:middle`} />;
}

function NativeOfflineAction({ offlineAction, livechat }: { offlineAction?: string; livechat: LiveChatConfig }) {
  const action = offlineAction ?? 'message';
  if (action === 'ticket') {
    return (
      <a href="#" onClick={(e) => { e.preventDefault(); navigateToView('tickets'); }}
        style="color:var(--sw-primary,#0d6efd)">
        {t('chat.leave_ticket', 'Leave us a ticket')}
      </a>
    );
  }
  if (action === 'message') {
    return (
      <a href="#" onClick={(e) => { e.preventDefault(); openChat(livechat); }}
        style="color:var(--sw-primary,#0d6efd)">
        {t('chat.leave_message', 'Leave a message')}
      </a>
    );
  }
  return null;
}
